import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types/user.types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateAccessToken: (token: string) => void;
  // Helpers de permissao
  canCreateRoom: () => boolean;
  canJoinRoom: () => boolean;
  canViewTranscriptions: () => boolean;
  canControlTranscription: () => boolean;
  canManageUsers: () => boolean;
  canManageTemplates: () => boolean;
  isAdmin: () => boolean;
  isAnalyst: () => boolean;
  isStakeholder: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
      updateAccessToken: (token) => set({ accessToken: token }),

      // Helpers de permissao por role:
      // - Admin: gerencia usuarios e templates, NAO pode criar/entrar em salas
      // - Analyst: cria/entra em salas, controla agente/transcricao
      // - Stakeholder: so entra em salas

      canCreateRoom: () => {
        const role = get().user?.role;
        return role === 'analyst'; // Somente analyst pode criar salas
      },
      canJoinRoom: () => {
        const role = get().user?.role;
        return role === 'analyst' || role === 'stakeholder'; // Analyst e stakeholder podem entrar
      },
      canViewTranscriptions: () => {
        const role = get().user?.role;
        return role === 'analyst'; // Somente analyst pode ver transcricoes
      },
      canControlTranscription: () => {
        const role = get().user?.role;
        return role === 'analyst'; // Somente analyst pode controlar transcricao
      },
      canManageUsers: () => {
        const role = get().user?.role;
        return role === 'admin'; // Somente admin pode gerenciar usuarios
      },
      canManageTemplates: () => {
        const role = get().user?.role;
        return role === 'admin'; // Somente admin pode gerenciar templates
      },
      isAdmin: () => get().user?.role === 'admin',
      isAnalyst: () => get().user?.role === 'analyst',
      isStakeholder: () => get().user?.role === 'stakeholder',
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      // Restaura isAuthenticated quando o store e rehidratado do localStorage
      onRehydrateStorage: () => (state) => {
        if (state?.user && state?.refreshToken) {
          state.isAuthenticated = true;
        }
      },
    }
  )
);
